import * as React from "react";
import {FormBodyProps} from "../../common/props/FormBodyProps";
import BrandForm from "../../brand/dto/BrandForm";
import InputFormGroup from "../../../hiberUiKit/src/molecules/FormGroup/InputFormGroup";
import CurrencyInputFormGroup from "../../ui/form/CurrencyInputFormGroup";
import RangeNumberInputFormGroup from "../../ui/form/RangeNumberInputFormGroup";
import Row from "../../layout/Row";
import Col from "../../layout/Col";
import AddressFormGroup from "../../address/form/component/AddressFormGroup";
import TextAreaFormGroup from "../../ui/form/TextAreaFormGroup";
import {none, option} from "ts-option";
import Attachment from "../../../model/attachment/Attachment";
import SingleFileDropDown from "../../file/SingleFileDropDown";
import AttachmentApi from "../../../api/AttachmentApi";
import CheckboxFormGroup from "../../ui/form/CheckboxFormGroup";
import {getLoggedUser} from "../../../store/view/user";
import DealerPricesMatrix from "../../brand/DealerPricesMatrix";
import {DealerPriceMatrixState} from "../../brand/dto/DealerPriceMatrixState";

interface ComponentProps {
	/**
	 * On add file
	 */
	onAddFile: (file: File) => any
	/**
	 * On remove file
	 */
	onRemoveFile: () => any
	/**
	 * On dealer price matrix change
	 */
	onDealerPriceMatrixChange: (state: DealerPriceMatrixState) => void
	/**
	 * On credit amount matrix change
	 */
	onCreditAmountMatrixChange: (state: DealerPriceMatrixState) => void
}

type Props = ComponentProps & FormBodyProps<BrandForm>;

export default class BrandFormBody extends React.PureComponent<Props> {
	/**
	 * If true, automatic payment limit field is displayed.
	 */
	automaticPaymentLimitInputVisible: boolean;

	/**
	 * If true, the credit value cap field is displayed. Gated like the automatic payment limit above it:
	 * both set how much money leaves the business, and neither is an account manager's to change.
	 */
	creditValueCapInputVisible: boolean;

	constructor(props: Props) {
		super(props);
		this.automaticPaymentLimitInputVisible = getLoggedUser().map(u => u.isAdmin()).getOrElse(false);
		this.creditValueCapInputVisible = this.automaticPaymentLimitInputVisible;
	}

	private renderSectionTitle(title: string, marginTop: string = "20px") {
		return <p style={{fontWeight: 700, fontSize: "15px", color: "#112363", marginBottom: "12px", marginTop, paddingBottom: "8px", borderBottom: "1px solid #d9d9d9"}}>{title}</p>;
	}

	private renderSubsectionTitle(title: string) {
		return <p style={{fontWeight: 600, color: "#112363", marginBottom: "10px", marginTop: "14px"}}>{title}</p>;
	}

	render() {
		const attachment = this.props.form.logoId.getOptionalValue().map(
			id => new Attachment(id, `${id}.png`, none, option(AttachmentApi.getAttachmentDownloadUrl(id)), none)
		);
		return (
			<div className={"c-create-brand"}>
				<Row>
					<Col lg={4} xl={4}>
						<InputFormGroup label="Brand name"
										placeholder="Enter brand name"
										field={this.props.form.brandName}
										onChange={this.props.onFieldChange}
										focus={this.props.form.hasFocus(this.props.form.brandName)}/>
					</Col>
					<Col lg={4} xl={4}>
						<AddressFormGroup label="Business address"
										  field={this.props.form.address}
										  onChange={field => {
											  field.getOptionalValue().isDefined ? this.props.onFieldCompleted(field) :
												  this.props.onFieldChange(field)
										  }}
										  form={this.props.form}
										  onFocusChange={this.props.onFocusChange}/>
					</Col>
					<Col lg={4} xl={4}>
						<InputFormGroup label="Phone number"
										placeholder="Enter brand phone number"
										field={this.props.form.phoneNumber}
										onChange={this.props.onFieldChange}
										focus={this.props.form.hasFocus(this.props.form.phoneNumber)}/>
					</Col>
				</Row>
				<Row>
					<Col lg={4} xl={4}>
						<InputFormGroup label="Email address"
										placeholder="Enter brand email address"
										field={this.props.form.email}
										onChange={this.props.onFieldChange}
										focus={this.props.form.hasFocus(this.props.form.email)}/>
					</Col>

					<Col lg={4} xl={4}>
						<InputFormGroup label="Gas safe number"
										placeholder="Enter Gas safe number"
										field={this.props.form.gasNumber}
										onChange={this.props.onFieldChange}
										focus={this.props.form.hasFocus(this.props.form.gasNumber)}/>
					</Col>
					<Col lg={4} xl={4}>
						<InputFormGroup label="Website"
										placeholder="Enter brand website"
										field={this.props.form.domain}
										onChange={this.props.onFieldChange}
										focus={this.props.form.hasFocus(this.props.form.domain)}/>
					</Col>
				</Row>
				<Row>
					<Col lg={4} xl={4}>
						<RangeNumberInputFormGroup field={this.props.form.customPartsMarkup}
												   minValue={0}
												   maxValue={100}
												   placeholder={"Enter custom parts markup"}
												   onChange={this.props.onFieldChange}
												   label={"Custom parts markup (%)"}
												   focus={this.props.form.hasFocus(this.props.form.customPartsMarkup)}
												   maxDecimalPlaces={2}/>
					</Col>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={this.props.form.labourCost}
												placeholder={"Enter labour cost"}
												onChange={this.props.onFieldChange}
												label={"Labour cost (€)"}
												focus={this.props.form.hasFocus(this.props.form.labourCost)}/>
					</Col>
					{this.automaticPaymentLimitInputVisible &&
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={this.props.form.automaticPaymentLimit}
												placeholder={"Enter automatic payment limit"}
												onChange={this.props.onFieldChange}
												label={"Automatic payment limit (€)"}
												focus={this.props.form.hasFocus(this.props.form.automaticPaymentLimit)}/>
					</Col>}
				</Row>
				<Row>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={this.props.form.maxFee}
												placeholder={"Enter Max fee"}
												onChange={this.props.onFieldChange}
												label={"Max fee (€)"}
												focus={this.props.form.hasFocus(this.props.form.maxFee)}/>
					</Col>
				</Row>
				<Row>
					<Col lg={4} xl={4}>
						<InputFormGroup label="VAT Registration Number"
										placeholder="Enter VAT Registration Number"
										field={this.props.form.vatRegistrationNumber}
										onChange={this.props.onFieldChange}
										focus={this.props.form.hasFocus(this.props.form.vatRegistrationNumber)}/>
					</Col>
					<Col lg={4} xl={4}>
						<RangeNumberInputFormGroup field={this.props.form.vatRate}
												   minValue={0}
												   maxValue={100}
												   placeholder={"Enter VAT rate"}
												   onChange={this.props.onFieldChange}
												   label={"VAT rate"}
												   focus={this.props.form.hasFocus(this.props.form.vatRate)}
												   maxDecimalPlaces={0}/>
					</Col>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={this.props.form.rewardPointValue}
												placeholder={"Enter single reward point value"}
												onChange={this.props.onFieldChange}
												label={"Reward point value (€)"}
												focus={this.props.form.hasFocus(this.props.form.rewardPointValue)}/>
					</Col>
				</Row>
				<Row>
					<Col xl={4} md={4}>
						<CheckboxFormGroup field={this.props.form.vatRegistered}
										   onChange={this.props.onFieldChange}
										   label={"VAT registered"}/>
					</Col>
				</Row>
				<div style={{background: '#f2f3f8', borderLeft: '3px solid #112363', borderRadius: '6px', padding: '16px 20px', marginTop: '20px', marginBottom: '8px'}}>
			{this.renderSectionTitle("Energy Credits", "0")}
				{this.renderSubsectionTitle("Registration")}
				<DealerPricesMatrix
					state={this.props.form.creditAmountMatrix}
					onChange={this.props.onCreditAmountMatrixChange}
					maxCellValue={9999999}
					rowLabel="credit amount row"
					error={this.props.form.getCreditAmountMatrixError()}/>
				{this.renderSubsectionTitle("Heatpump")}
				<Row>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={this.props.form.creditAmountAirToWaterHp}
												placeholder={"Air to Water"}
												onChange={this.props.onFieldChange}
												label={"Air to Water"}
												focus={this.props.form.hasFocus(this.props.form.creditAmountAirToWaterHp)}/>
					</Col>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={this.props.form.creditAmountAirToAirHp}
												placeholder={"Air to Air"}
												onChange={this.props.onFieldChange}
												label={"Air to Air"}
												focus={this.props.form.hasFocus(this.props.form.creditAmountAirToAirHp)}/>
					</Col>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={this.props.form.creditAmountWaterToWaterHp}
												placeholder={"Water to Water"}
												onChange={this.props.onFieldChange}
												label={"Water to Water"}
												focus={this.props.form.hasFocus(this.props.form.creditAmountWaterToWaterHp)}/>
					</Col>
				</Row>
				<Row>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={this.props.form.creditAmountBrineToWaterHp}
												placeholder={"Brine to Water"}
												onChange={this.props.onFieldChange}
												label={"Brine to Water"}
												focus={this.props.form.hasFocus(this.props.form.creditAmountBrineToWaterHp)}/>
					</Col>
				</Row>
				{this.renderSubsectionTitle("Flowsave")}
				<Row>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={this.props.form.creditAmountFlowsave}
												placeholder={"Flowsave"}
												onChange={this.props.onFieldChange}
												label={"Flowsave"}
												focus={this.props.form.hasFocus(this.props.form.creditAmountFlowsave)}/>
					</Col>
				</Row>
				</div>
				<div style={{background: '#f2f3f8', borderLeft: '3px solid #112363', borderRadius: '6px', padding: '16px 20px', marginTop: '20px', marginBottom: '8px'}}>
				{this.renderSectionTitle("Payout Amounts", "0")}
				{this.renderSubsectionTitle("Registration")}
				<DealerPricesMatrix
					state={this.props.form.dealerPriceMatrix}
					onChange={this.props.onDealerPriceMatrixChange}
					currencySymbol="€"
					error={this.props.form.getDealerPriceMatrixError()}/>
				{this.renderSubsectionTitle("Heatpump")}
				<Row>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={this.props.form.creditValueAirToWaterHp}
												placeholder={"Air to Water"}
												onChange={this.props.onFieldChange}
												label={"Air to Water (€)"}
												focus={this.props.form.hasFocus(this.props.form.creditValueAirToWaterHp)}/>
					</Col>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={this.props.form.creditValueAirToAirHp}
												placeholder={"Air to Air"}
												onChange={this.props.onFieldChange}
												label={"Air to Air (€)"}
												focus={this.props.form.hasFocus(this.props.form.creditValueAirToAirHp)}/>
					</Col>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={this.props.form.creditValueWaterToWaterHp}
												placeholder={"Water to Water"}
												onChange={this.props.onFieldChange}
												label={"Water to Water (€)"}
												focus={this.props.form.hasFocus(this.props.form.creditValueWaterToWaterHp)}/>
					</Col>
				</Row>
				<Row>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={this.props.form.creditValueBrineToWaterHp}
												placeholder={"Brine to Water"}
												onChange={this.props.onFieldChange}
												label={"Brine to Water (€)"}
												focus={this.props.form.hasFocus(this.props.form.creditValueBrineToWaterHp)}/>
					</Col>
				</Row>
				{this.renderSubsectionTitle("Flowsave")}
				<Row>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={this.props.form.creditValueFlowsave}
												placeholder={"Flowsave"}
												onChange={this.props.onFieldChange}
												label={"Flowsave (€)"}
												focus={this.props.form.hasFocus(this.props.form.creditValueFlowsave)}/>
					</Col>
				</Row>
				{this.creditValueCapInputVisible &&
				<React.Fragment>
					{this.renderSubsectionTitle("Service")}
					<Row>
						<Col lg={4} xl={4}>
							<CurrencyInputFormGroup field={this.props.form.creditValueCap}
													placeholder={"Enter credit value cap"}
													onChange={this.props.onFieldChange}
													label={"Credit value cap (€)"}
													focus={this.props.form.hasFocus(this.props.form.creditValueCap)}/>
							<p style={{fontSize: "12.5px", color: "#5b6478", marginTop: "-8px"}}>
								The most a single mileage claim can pay out. Applies to every partner that does not
								override it.
							</p>
						</Col>
					</Row>
				</React.Fragment>}
				</div>
				<Row>
					<Col lg={12} xl={12}>
						<TextAreaFormGroup label="Notes"
										   placeholder="Brand notes"
										   field={this.props.form.freeText}
										   onChange={this.props.onFieldChange}
										   focus={this.props.form.hasFocus(this.props.form.freeText)}
										   rows={6}/>
					</Col>
				</Row>
				<Row>
					<Col xl={12} md={12}>
						<div className={"c-brand-form_dropdown-label"}>Image</div>
						<div className={"c-brand-form_dropdown-wrapper"}>
							<SingleFileDropDown attachment={attachment}
												onDropFile={this.props.onAddFile}
												onRemove={this.props.onRemoveFile}/>
						</div>
					</Col>
				</Row>
			</div>
		);
	}
}
